import type { ClassValue } from "clsx";

import clsx from "clsx";

export type ButtonVariant =
  | "brand"
  | "neutral"
  | "positive"
  | "negative"
  | "tonal"
  | "outline"
  | "text";

export type ButtonSize = "2xl" | "xl" | "lg" | "md" | "sm" | "xs";

// Base styles shared across all buttons
const baseDefault = [
  "inline-flex",
  "items-center",
  "justify-center",
  "rounded-lg",
  "transition-colors",
  "duration-200",
  "overflow-hidden",
  "relative",
] as const;

const baseDisabled = ["bg-surface-disabled", "text-text-disabled"] as const;

const baseFocused = [
  "focus-visible:ring-2",
  "focus-visible:ring-offset-2",
  "focus-visible:ring-stroke-focused",
] as const;

const buttonVariants = {
  brand: ["bg-surface-brand-default", "text-text-inverse-primary"],
  neutral: ["bg-surface-inverse-primary", "text-text-inverse-primary"],
  positive: ["bg-surface-positive-default", "text-text-inverse-primary"],
  negative: ["bg-surface-negative-default", "text-text-inverse-primary"],
  tonal: ["bg-surface-tonal-default", "text-text-primary"],
  outline: [
    "bg-surface-transparent",
    "border",
    "border-solid",
    "border-stroke-primary",
    "text-text-primary",
  ],
  text: ["bg-surface-transparent", "text-text-primary"],
} as const satisfies Record<ButtonVariant, readonly string[]>;

const textColors = {
  brand: ["text-text-inverse-primary"],
  neutral: ["text-text-inverse-primary"],
  positive: ["text-text-inverse-primary"],
  negative: ["text-text-inverse-primary"],
  tonal: ["text-text-primary"],
  outline: ["text-text-primary"],
  text: ["text-text-primary"],
} as const satisfies Record<ButtonVariant, readonly string[]>;

const sizes = {
  "2xl": ["h-[64px]", "min-w-[128px]"],
  xl: ["h-[56px]", "min-w-[112px]"],
  lg: ["h-[48px]", "min-w-[96px]"],
  md: ["h-[40px]", "min-w-[80px]"],
  sm: ["h-[32px]", "min-w-[64px]"],
  xs: ["h-[24px]", "min-w-[48px]"],
} as const satisfies Record<ButtonSize, readonly string[]>;

const paddingSizes = {
  "2xl": ["py-[16px]", "px-[20px]"],
  xl: ["py-[16px]", "px-[20px]"],
  lg: ["py-[12px]", "px-[16px]"],
  md: ["py-[8px]", "px-[12px]"],
  sm: ["py-[8px]", "px-[12px]"],
  xs: ["py-[4px]", "px-[8px]"],
} as const satisfies Record<ButtonSize, readonly string[]>;

const typoSizes = {
  "2xl": ["typo-label-lg"],
  xl: ["typo-label-md"],
  lg: ["typo-label-sm"],
  md: ["typo-label-sm"],
  sm: ["typo-label-xs"],
  xs: ["typo-label-2xs"],
} as const satisfies Record<ButtonSize, readonly string[]>;

const gapSizes = {
  "2xl": ["gap-[8px]"],
  xl: ["gap-[8px]"],
  lg: ["gap-[8px]"],
  md: ["gap-[8px]"],
  sm: ["gap-[4px]"],
  xs: ["gap-[4px]"],
} as const satisfies Record<ButtonSize, readonly string[]>;

const iconSizes = {
  "2xl": ["w-[24px]", "h-[24px]"],
  xl: ["w-[20px]", "h-[20px]"],
  lg: ["w-[20px]", "h-[20px]"],
  md: ["w-[16px]", "h-[16px]"],
  sm: ["w-[16px]", "h-[16px]"],
  xs: ["w-[12px]", "h-[12px]"],
} as const satisfies Record<ButtonSize, readonly string[]>;

// Hover and active styles
const baseHoverAndActive = [
  "transition-colors",
  "duration-200",
  "bg-transparent",
];

const variantHoverAndActive = {
  brand: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
  neutral: [
    "hover:bg-overlay-opacity-state-inverse-hover",
    "active:bg-overlay-opacity-state-inverse-pressed",
  ],
  positive: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
  negative: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
  tonal: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
  outline: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
  text: [
    "hover:bg-overlay-opacity-state-hover",
    "active:bg-overlay-opacity-state-pressed",
  ],
} as const satisfies Record<ButtonVariant, readonly string[]>;

const baseInternalContainerStyles = [
  "flex",
  "flex-1",
  "items-center",
  "justify-center",
  "w-full",
  "h-full",
];

const baseLoaderContainerStyles = [
  "absolute",
  "inset-0",
  "flex",
  "items-center",
  "justify-center",
  "animate-spin",
];

const baseIconSlotStyles = [
  "inline-flex",
  "shrink-0",
  "items-center",
  "justify-center",
  "[&>svg]:h-full",
  "[&>svg]:w-full",
];

// Composable button classes
export const buttonStyles = {
  container: ({
    variant = "brand",
    size = "md",
    isDisabled = false,
    isLoading = false,
    className,
  }: {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isDisabled?: boolean;
    isLoading?: boolean;
    className?: ClassValue;
  }) =>
    clsx(
      baseDefault,
      !(isLoading || isDisabled) && buttonVariants[variant],
      sizes[size],
      isDisabled && baseDisabled,
      baseFocused,
      className,
    ),

  internal: ({
    variant,
    size = "md",
    isDisabled,
    isLoading,
    className,
  }: {
    variant: ButtonVariant;
    size?: ButtonSize;
    isDisabled?: boolean;
    isLoading?: boolean;
    className?: ClassValue;
  }) =>
    clsx(
      baseInternalContainerStyles,
      paddingSizes[size],
      typoSizes[size],
      gapSizes[size],
      isLoading && "opacity-0",
      isDisabled && baseDisabled,
      !isDisabled &&
        clsx(
          baseHoverAndActive,
          variantHoverAndActive[variant],
          textColors[variant],
        ),
      className,
    ),

  loaderContainer: ({ className }: { className?: ClassValue }) =>
    clsx(baseLoaderContainerStyles, className),

  loaderIcon: (size: ButtonSize) => clsx("text-icon-primary", iconSizes[size]),

  icon: (size: ButtonSize) => clsx(baseIconSlotStyles, iconSizes[size]),
};
